import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useCreateDomain, useUpdateDomain } from '../hooks/useDomains';
import { DomainFormData, TenantRouteParams } from '../types';
import { DomainTableData } from '../types';

interface AddDomainDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: DomainTableData | null;
  onSuccess?: () => void;
}

export const AddDomainDialog: React.FC<AddDomainDialogProps> = ({
  open,
  onClose,
  editingItem,
  onSuccess,
}) => {
  const { tenantId } = useParams<TenantRouteParams>();
  const isEditing = !!editingItem;
  const createDomainMutation = useCreateDomain(tenantId!);
  const updateDomainMutation = useUpdateDomain(tenantId!);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<DomainFormData>({
    mode: 'onChange',
    defaultValues: {
      name: editingItem?.name || '',
      description: editingItem?.description || '',
      code: editingItem?.code || '',
    },
  });

  // Reset form when dialog opens/closes or editing item changes
  React.useEffect(() => {
    if (open) {
      reset({
        name: editingItem?.name || '',
        description: editingItem?.description || '',
        code: editingItem?.code || '',
      });
    }
  }, [open, editingItem, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: DomainFormData) => {
    try {
      if (isEditing && editingItem) {
        await updateDomainMutation.mutateAsync({
          domainId: editingItem.id,
          data: {
            name: data.name,
            description: data.description || undefined,
            code: data.code || undefined,
          },
        });
      } else {
        await createDomainMutation.mutateAsync({
          name: data.name,
          description: data.description || undefined,
          code: data.code || undefined,
        });
      }

      // Close dialog and reset form on success
      handleClose();
      onSuccess?.();
    } catch (error) {
      // Error handling is done in the mutation hooks via notifications
      console.error('Failed to save domain:', error);
    }
  };

  const isLoading = createDomainMutation.isPending || updateDomainMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>{isEditing ? 'Edit Institute' : 'Add New Institute'}</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Institute name is required',
                minLength: {
                  value: 2,
                  message: 'Institute name must be at least 2 characters',
                },
                maxLength: {
                  value: 100,
                  message: 'Institute name must not exceed 100 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Institute Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- Dialog input: focus first field on open
                  autoFocus
                  placeholder="e.g., Institute of Engineering"
                />
              )}
            />

            <Controller
              name="code"
              control={control}
              rules={{
                maxLength: {
                  value: 20,
                  message: 'Institute code must not exceed 20 characters',
                },
                pattern: {
                  value: /^[A-Z0-9_-]*$/,
                  message:
                    'Institute code must contain only uppercase letters, numbers, hyphens, and underscores',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Institute Code"
                  variant="outlined"
                  fullWidth
                  error={!!errors.code}
                  helperText={
                    errors.code?.message ||
                    'Optional short code for the institute (e.g., ENG, MED, LAW)'
                  }
                  disabled={isLoading}
                  placeholder="e.g., ENG"
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{
                maxLength: {
                  value: 500,
                  message: 'Description must not exceed 500 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isLoading}
                  placeholder="Optional description for the institute..."
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isLoading} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {isLoading
              ? isEditing
                ? 'Updating...'
                : 'Creating...'
              : isEditing
                ? 'Update Institute'
                : 'Create Institute'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
